// @flow
import APIError from 'mz-sdk/client/APIError'
import is from 'mz-utils/is'
import { isMessageObject } from 'mz-intl'
import maybeMap from 'mz-utils/maybeMap'
import deepForEach from 'mz-utils/deepForEach'
import type { Message } from 'mz-intl'
import messages from '../messages.intl'

// Types
export type ValidationResultType = {
  warning?: string | Message,
  error?: string | Message,
  info?: string | Message,
}
export type ValidationResultsType = {
  warnings: Array<string | Message>,
  errors: Array<string | Message>,
  info: Array<string | Message>,
}

const getUserMessage = (err: ?any, isApiError: boolean) => {
  let message = ''
  if (!err) return message

  if (isApiError) {
    message = err.i18n || err.user_message
  } else {
    message = err.user_message || err.message
  }
  return message || messages.UNKNOWN_ERROR
}

const expandErrorObject = (error: Object, isApiError: boolean) => {
  if (error.message) {
    return getUserMessage(error, isApiError)
  }
  return Object.keys(error).map(k => {
    if (is.object(error[k])) {
      return expandErrorObject(error[k], isApiError)
    }
    return maybeMap(error[k], x => getUserMessage(x, isApiError))
  })
}

const expandErrorMessage = (errors: any): Array<any> => {
  const res = []
  deepForEach(
    maybeMap(errors, (err: ?any) => {
      if (!err) return null
      const isApiError = err instanceof APIError
      if (isApiError) {
        return expandErrorObject(err.response, true)
      }
      if (isMessageObject(err)) {
        return err
      }
      if (err instanceof SyntaxError) {
        return messages.UNKNOWN_ERROR
      }

      const { message } = (err: Error)
      if (is.string(message)) {
        return message
      }
      if (is.object(message)) {
        return expandErrorObject((message: any), false)
      }
      if (is.string(err) || is.object(err)) {
        return err
      }
      return null
    }),
    x => !!x && res.push(x)
  )
  return res
}

const normalizeValidationItem = (itm?: any): ValidationResultType => {
  if (
    itm &&
    is.object(itm) &&
    ('error' in itm || 'warning' in itm || 'info' in itm)
  ) {
    return itm
  }
  return { error: itm || messages.UNKNOWN_ERROR }
}

const uniqueValidationResult = (
  result: ValidationResultsType
): ValidationResultsType => ({
  errors: Array.from(new Set(result.errors)),
  warnings: Array.from(new Set(result.warnings)),
  info: Array.from(new Set(result.info)),
})

const normalizeErrors = (errors?: any): ValidationResultsType => {
  const expandedErrors = expandErrorMessage(errors)
  const normErrors = maybeMap(expandedErrors, normalizeValidationItem).filter(
    x => x
  )

  const validationRes = normErrors.reduce(
    (res, x: any) => {
      if ('error' in x) res.errors.push(expandErrorMessage(x.error)[0])
      else if ('warning' in x)
        res.warnings.push(expandErrorMessage(x.warning)[0])
      else if ('info' in x) res.info.push(expandErrorMessage(x.info)[0])
      return res
    },
    { errors: [], warnings: [], info: [] }
  )

  return uniqueValidationResult(validationRes)
}

export default normalizeErrors
